/**
 * Workspace selector — shown after login when no workspace is selected.
 *
 * Lists all workspaces the current user has membership in.
 * Clicking a workspace loads its detail and navigates to Overview.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspace } from '../hooks/useWorkspace';
import { useAdminAuth } from '../auth/AdminAuthProvider';
import * as api from '../api-client';
import { MatcoraBrandMark } from '../components/MatcoraBrand';
import { AdminEmptyState, AdminPanel } from '../components/AdminPrimitives';
 
export function WorkspaceSelectorPage() {
  const { selectWorkspace } = useWorkspace();
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<api.WorkspaceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await api.listWorkspaces();
        const invalidWorkspace = list.find((workspace) => !workspace.public_workspace_id);
        if (invalidWorkspace) {
          throw new Error('Workspace configuration is incomplete');
        }
        if (!cancelled) {
          setWorkspaces(list);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof api.AdminApiError
              ? err.detail
              : err instanceof Error
                ? err.message
                : 'Failed to load workspaces',
          );
          setIsLoading(false);
        }
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  async function handleSelect(workspace: api.WorkspaceSummary) {
    setSelecting(workspace.public_workspace_id);
    setError(null);
    try {
      await selectWorkspace(workspace.workspace_key, workspace.public_workspace_id);
      navigate(`/workspaces/${encodeURIComponent(workspace.public_workspace_id)}/overview`);
    } catch {
      setError('Failed to load workspace');
      setSelecting(null);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-primary)] p-4">
      <div className="max-w-2xl w-full relative">
        <div className="mb-6 text-center">
          <MatcoraBrandMark className="mx-auto h-20 w-20 shadow-[0_12px_24px_rgba(15,23,42,0.14)]" />
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-[var(--text-primary)]">
            Welcome{user?.displayName ? `, ${user.displayName}` : ''}
          </h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Choose the workspace you want to manage.
          </p>
        </div>

        {error && (
          <div role="alert" className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]">
            {error}
          </div>
        )}

        <AdminPanel
          title="Your Workspaces"
          subtitle="Access is scoped to workspaces where you already have an admin membership."
        >
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-24 rounded-[1.2rem] bg-[var(--surface-tertiary)] animate-pulse" />
              ))}
            </div>
          ) : workspaces.length === 0 ? (
            <AdminEmptyState
              title="No workspaces found"
              description="Ask a workspace owner to invite you before signing in again."
            />
          ) : (
            <div className="space-y-3">
              {workspaces.map((ws) => (
                <button
                  key={ws.workspace_key}
                  type="button"
                  onClick={() => handleSelect(ws)}
                  disabled={selecting !== null}
                  className={`
                    w-full flex items-center gap-4 rounded-[1.2rem] border border-[var(--border-default)]
                    bg-white px-4 py-4 text-left shadow-[0_10px_22px_rgba(15,23,42,0.04)]
                    transition-all duration-200 hover:border-[var(--accent-primary)] hover:shadow-[0_14px_28px_rgba(15,23,42,0.06)]
                    disabled:cursor-not-allowed disabled:opacity-50
                  `}
                >
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--surface-tertiary)] shadow-sm">
                    <span className="text-lg font-semibold text-[var(--accent-primary)]">
                      {ws.display_name[0].toUpperCase()}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[var(--text-primary)] truncate">
                      {ws.display_name}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
                      {ws.role} · {ws.capabilities.length} capabilities
                    </p>
                  </div>

                  {selecting === ws.public_workspace_id ? <Spinner /> : <ChevronRightIcon />}
                </button>
              ))}
            </div>
          )}
        </AdminPanel>
      </div>
    </div>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-5 h-5 text-[var(--text-tertiary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="w-5 h-5 animate-spin text-[var(--accent-primary)]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}
